/**
 * Shared formatters, helpers and constants used across the public
 * `/book` surface. Extracted from PublicBook.jsx in v3.9.4 to keep
 * page files lean and helper logic testable/reusable.
 */

export const isoToday = () => new Date().toISOString().slice(0, 10);

export const isoInDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const formatEU = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

export const fmtEUR = (n) =>
  new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

/** True when a HH:MM time falls inside a closed [from..to] window.
 *  Cross-midnight windows are supported (from > to). */
export function timeInBlackout(t, from, to) {
  if (!t || !from || !to) return false;
  try {
    const toMin = (s) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
    const T = toMin(t), F = toMin(from), U = toMin(to);
    if (F === U) return false;
    if (F < U) return F <= T && T < U;
    return T >= F || T < U;
  } catch {
    return false;
  }
}

/** Turn stored (`gasoline_hybrid`) or legacy (`Petrol`) values into a
 *  human label for the public vehicle card tags. */
const FUEL_PRETTY = {
  gasoline: "Gasoline", diesel: "Diesel",
  gasoline_hybrid: "Gasoline Hybrid", diesel_hybrid: "Diesel Hybrid",
  plug_in_hybrid: "Plug-in Hybrid", electric: "Electric",
};
const TRANS_PRETTY = {
  automatic: "Automatic", semi_automatic: "Semi-Automatic", manual: "Manual",
};

export function prettyFuel(raw) {
  if (!raw) return "";
  const key = String(raw).toLowerCase().replace(/[\s-]+/g, "_");
  if (FUEL_PRETTY[key]) return FUEL_PRETTY[key];
  if (["petrol", "gas", "unleaded"].includes(key)) return "Gasoline";
  if (["ev", "bev"].includes(key)) return "Electric";
  if (key === "phev") return "Plug-in Hybrid";
  if (key === "hybrid") return "Gasoline Hybrid";
  return raw;
}

export function prettyTransmission(raw) {
  if (!raw) return "Manual";
  const key = String(raw).toLowerCase().replace(/[\s-]+/g, "_");
  if (TRANS_PRETTY[key]) return TRANS_PRETTY[key];
  if (["at", "auto"].includes(key)) return "Automatic";
  if (["mt"].includes(key)) return "Manual";
  if (["dsg", "cvt", "semi"].includes(key)) return "Semi-Automatic";
  return raw;
}

export const CATEGORY_ORDER = ["economy", "compact", "sports", "suv", "luxury", "electric"];

// Field-status helpers driven by the shared checkout_fields config.
export const isActiveStatus = (s) => s === "required" || s === "optional";
export const isRequiredStatus = (s) => s === "required";

// Optional-but-active field descriptors we render dynamically when the
// server-side checkout_fields config marks them as active.
// `type: "country"` is v3.9.7 — replaces the free-text Country input
// with an alphabetised <select>. `type: "textarea"` renders wide (spans
// the full row) so the label & box stay visually aligned with the grid
// inputs above.
export const OPTIONAL_FIELDS = [
  { key: "address",         label: "Address",              type: "text" },
  { key: "city",            label: "City",                 type: "text" },
  { key: "zip",             label: "ZIP / Postcode",       type: "text" },
  { key: "country",         label: "Country",              type: "country" },
  { key: "id_passport",     label: "ID / Passport",        type: "text" },
  { key: "license_number",  label: "Driver's license #",   type: "text" },
  { key: "driver_age",      label: "Driver age",           type: "number" },
  { key: "notes",           label: "Notes",                type: "textarea" },
];

export function requiredLabel(label, required) {
  return required ? `${label} *` : label;
}
